import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { type ChiamataBc, useInviaABc, useInviiBc } from '../api/ordini.js';
import { dataOra } from '../formati.js';
import { t } from '../i18n/lingua.js';

/**
 * Business Central, dalla scheda dell'ordine.
 *
 * Nel v2 un invio a BC lascia un avviso a chi è davanti allo schermo e
 * nient'altro: se un aggiornamento non arriva non lo sa nessuno, e il CRM e
 * BC cominciano a dire due cose diverse. Qui c'è il registro di ogni invio,
 * chiamata per chiamata, con quello che BC ha risposto — ed è anche quello
 * che serve nelle prove, per dire a chi guarda in BC quale `doc` cercare.
 */

const MOTIVI: Record<string, string> = {
  state: t('cambio di stato'),
  button: t('a mano'),
  'mass-state-2': t('«Stato 2» di massa'),
  carrier: t('invio al vettore'),
  'mass-carrier': t('invio al vettore di massa'),
  'cl-tracking': t('tracking C&L'),
  interglobo: t('movimento Interglobo'),
  'status-refresh': t('«Aggiorna gli stati» da una data'),
};

const ESITI: Record<ChiamataBc['outcome'], { classe: string; testo: string }> = {
  ok: { classe: 'badge-ok', testo: t('accettata') },
  refused: { classe: 'badge-alert', testo: t('rifiutata') },
  failed: { classe: 'badge-alert', testo: t('non arrivata') },
  skipped: { classe: '', testo: t('non partita') },
};

/** Le chiamate raggruppate per invio, il più recente in cima. */
function perInvio(chiamate: ChiamataBc[]): ChiamataBc[][] {
  const gruppi = new Map<string, ChiamataBc[]>();
  for (const c of chiamate) {
    const gruppo = gruppi.get(c.batch) ?? [];
    gruppo.push(c);
    gruppi.set(c.batch, gruppo);
  }
  return [...gruppi.values()].map((g) => [...g].sort((a, b) => a.id - b.id));
}

function valori(parametri: Record<string, unknown>): string {
  return Object.entries(parametri)
    .map(([k, v]) => `${k}=${v === '' || v === null || v === undefined ? '∅' : String(v)}`)
    .join('  ');
}

export function InviiBc({ ordineId }: { ordineId: number }) {
  const invii = useInviiBc(ordineId, true);
  const invia = useInviaABc(ordineId);
  const [esito, setEsito] = useState<string | null>(null);
  const [domanda, setDomanda] = useState<string | null>(null);

  const manda = (operation: 'insert' | 'update', confirm?: boolean) => {
    setEsito(null);
    setDomanda(null);
    invia.mutate(
      { operation, confirm },
      {
        onSuccess: (r) => setEsito(r.message),
        onError: (errore) => {
          if (errore instanceof ErroreApi && errore.codice === 'serveConferma') {
            setDomanda(errore.message);
          } else {
            setEsito(errore instanceof ErroreApi ? errore.message : t('Non è stato possibile mandarlo.'));
          }
        },
      },
    );
  };

  const modo = invii.data?.mode;
  const gruppi = perInvio(invii.data?.sends ?? []);

  return (
    <div className="card" style={{ margin: '14px 20px 24px' }}>
      <div className="card-head">
        <span className="card-title">{t('Invii a Business Central')}</span>
        {modo === 'prova' && (
          <span className="badge badge-warn" title={t('Fino al passaggio partono solo gli ordini dei clienti di prova, con il numero spostato.')}>
            {t('in prova')}
          </span>
        )}
        {modo === 'spento' && <span className="badge">{t('invio spento')}</span>}
        {modo === 'acceso' && <span className="badge badge-ok">{t('invio acceso')}</span>}
        {modo && modo !== 'spento' && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn" disabled={invia.isPending} onClick={() => manda('update')}>
              {t('Manda l’aggiornamento')}
            </button>
            <button className="btn" disabled={invia.isPending} onClick={() => manda('insert')}>
              {t('Manda l’inserimento')}
            </button>
          </span>
        )}
      </div>

      {domanda && (
        <div className="avviso-riga">
          <span>{domanda}</span>
          <button className="btn btn-ghost" onClick={() => setDomanda(null)}>
            {t('Lascia stare')}
          </button>
          <button className="btn" onClick={() => manda('insert', true)}>
            {t('Inseriscilo lo stesso')}
          </button>
        </div>
      )}
      {invia.isPending && <div className="avviso-riga">{t('Invio…')}</div>}
      {esito && (
        <div className="avviso-riga">
          <span>{esito}</span>
          <button className="btn btn-ghost" onClick={() => setEsito(null)}>
            {t('Ho letto')}
          </button>
        </div>
      )}

      <div className="card-body">
        {gruppi.length === 0 ? (
          <p className="dim" style={{ margin: 0, fontSize: 13 }}>
            {invii.isLoading
              ? t('Un momento…')
              : t('Da questo CRM non è ancora partito niente per Business Central.')}
          </p>
        ) : (
          gruppi.map((g) => {
            const primo = g[0]!;
            const accettate = g.filter((c) => c.outcome === 'ok').length;
            return (
              <div key={primo.batch} style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 13 }}>
                  <strong>{dataOra(primo.createdAt)}</strong>
                  <span>{primo.operation === 'insert' ? t('inserimento') : t('aggiornamento')}</span>
                  <span className="dim">{MOTIVI[primo.trigger] ?? primo.trigger}</span>
                  <span className="dim" style={{ fontFamily: 'var(--mono)' }}>doc {primo.doc}</span>
                  {primo.mode === 'test' && <span className="badge badge-warn">{t('prova')}</span>}
                  <span className={`badge ${accettate === g.length ? 'badge-ok' : 'badge-alert'}`}>
                    {accettate}/{g.length} {t('accettate')}
                  </span>
                </div>
                <table className="table" style={{ marginTop: 6, fontSize: 12 }}>
                  <tbody>
                    {g.map((c) => (
                      <tr key={c.id}>
                        <td style={{ width: 40 }}>{c.bcCompany === 2 ? 'Inc' : 'SPA'}</td>
                        <td style={{ fontFamily: 'var(--mono)', width: 190 }}>
                          {c.method}
                          {c.pass !== 'default' && <div className="dim">{c.pass}</div>}
                        </td>
                        <td style={{ width: 90 }}>
                          <span className={`badge ${ESITI[c.outcome].classe}`}>{ESITI[c.outcome].testo}</span>
                        </td>
                        <td>
                          {c.error ?? c.returnValue ?? ''}
                          <details>
                            <summary className="dim">{t('cosa è partito')}</summary>
                            <span style={{ fontFamily: 'var(--mono)', wordBreak: 'break-word' }}>
                              {valori(c.params)}
                            </span>
                          </details>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
